// Pure logic behind the partner custom-model pricing, diagnostics, and edit
// forms.
//
// It lives outside the component on purpose. The gate suite cannot render React:
// `vitest.config.ts` is `environment: 'node'` with no per-file override, and
// jsdom / happy-dom / @testing-library are not installed anywhere in the repo
// (`packages/admin-web` has no test script at all). So anything worth asserting
// has to be a function the node suite can call — the pattern
// `tests/admin-web/admin-web.test.ts` already uses for `applyTableState` and
// `describeError`.
//
// The billing-mode derivation is imported from shared-types rather than
// reimplemented: a form that showed "free" while the server stored
// `platform-credit` would be a billing bug the operator could not see.

import {
  partnerPricingBillingMode,
  type BillingMode,
  type PartnerCustomModelPricing,
  type PartnerCustomModelPricingInput,
  type PartnerCustomModelSummary
} from '../../api.js';
import type { ThinkingLevel } from '../../types.js';

/** Mirrors `MAX_SUPPORTED_DECIMAL_PLACES` in the server's credit store. Rates
 *  beyond this are rejected rather than rounded, on both sides. */
const MAX_DECIMAL_PLACES = 12;

export type PricingValidationError =
  | 'rate_not_a_number'
  | 'rate_negative'
  | 'mixed_zero_rate'
  | 'free_model_cache_rate'
  | 'cached_input_above_input'
  | 'margin_not_positive'
  | 'unsupported_decimal';

export type PricingValidation =
  | { ok: true; pricing: PartnerCustomModelPricing; billingMode: BillingMode }
  | { ok: false; error: PricingValidationError };

export interface PricingFormInput {
  inputUsdPerMillion: string;
  outputUsdPerMillion: string;
  cachedInputUsdPerMillion?: string;
  cacheWriteInputUsdPerMillion?: string;
  marginMultiplier: string;
}

function decimalPlaces(value: number): number {
  const match = /^\d+(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(String(value));
  if (!match) return Number.POSITIVE_INFINITY;
  return Math.max(0, (match[1]?.length ?? 0) - Number(match[2] ?? 0));
}

function parseRate(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Validate the three rate fields exactly as the server does, so an operator sees
 * the rejection before submitting rather than as a translated 400.
 *
 * Both rates zero is valid and means free. One of them zero is not: it has no
 * coherent billing meaning, and accepting it would make the derived mode depend
 * on which field the operator happened to fill in.
 */
export function validatePricingInput(
  input: PricingFormInput
): PricingValidation {
  const inputRate = parseRate(input.inputUsdPerMillion);
  const outputRate = parseRate(input.outputUsdPerMillion);
  if (inputRate === undefined || outputRate === undefined) {
    return { ok: false, error: 'rate_not_a_number' };
  }
  if (inputRate < 0 || outputRate < 0) {
    return { ok: false, error: 'rate_negative' };
  }
  if ((inputRate === 0) !== (outputRate === 0)) {
    return { ok: false, error: 'mixed_zero_rate' };
  }
  const cachedInputRaw = input.cachedInputUsdPerMillion?.trim() ?? '';
  const cacheWriteInputRaw = input.cacheWriteInputUsdPerMillion?.trim() ?? '';
  const cachedInputRate = cachedInputRaw ? parseRate(cachedInputRaw) : undefined;
  const cacheWriteInputRate = cacheWriteInputRaw ? parseRate(cacheWriteInputRaw) : undefined;
  if (
    (cachedInputRaw && cachedInputRate === undefined) ||
    (cacheWriteInputRaw && cacheWriteInputRate === undefined)
  ) return { ok: false, error: 'rate_not_a_number' };
  if ((cachedInputRate ?? 0) < 0 || (cacheWriteInputRate ?? 0) < 0) {
    return { ok: false, error: 'rate_negative' };
  }
  if (inputRate === 0 && (cachedInputRaw || cacheWriteInputRaw)) {
    return { ok: false, error: 'free_model_cache_rate' };
  }
  if (cachedInputRate !== undefined && cachedInputRate > inputRate) {
    return { ok: false, error: 'cached_input_above_input' };
  }
  const marginRaw = input.marginMultiplier.trim();
  const margin = marginRaw ? parseRate(marginRaw) : 1;
  if (margin === undefined) return { ok: false, error: 'rate_not_a_number' };
  if (margin <= 0) return { ok: false, error: 'margin_not_positive' };
  if (
    decimalPlaces(inputRate) > MAX_DECIMAL_PLACES ||
    decimalPlaces(outputRate) > MAX_DECIMAL_PLACES ||
    (cachedInputRate !== undefined && decimalPlaces(cachedInputRate) > MAX_DECIMAL_PLACES) ||
    (cacheWriteInputRate !== undefined && decimalPlaces(cacheWriteInputRate) > MAX_DECIMAL_PLACES) ||
    decimalPlaces(margin) > MAX_DECIMAL_PLACES
  ) {
    return { ok: false, error: 'unsupported_decimal' };
  }
  const pricing: PartnerCustomModelPricing = {
    inputUsdPerMillion: inputRate,
    outputUsdPerMillion: outputRate,
    ...(cachedInputRate !== undefined ? { cachedInputUsdPerMillion: cachedInputRate } : {}),
    ...(cacheWriteInputRate !== undefined ? { cacheWriteInputUsdPerMillion: cacheWriteInputRate } : {}),
    marginMultiplier: margin
  };
  return {
    ok: true,
    pricing,
    billingMode: partnerPricingBillingMode(pricing)
  };
}

/** The request-shaped pricing, with the margin omitted when it is the default —
 *  the server normalizes an absent margin to 1. */
export function pricingRequestFrom(
  pricing: PartnerCustomModelPricing
): PartnerCustomModelPricingInput {
  return {
    inputUsdPerMillion: pricing.inputUsdPerMillion,
    outputUsdPerMillion: pricing.outputUsdPerMillion,
    ...(pricing.cachedInputUsdPerMillion !== undefined ? { cachedInputUsdPerMillion: pricing.cachedInputUsdPerMillion } : {}),
    ...(pricing.cacheWriteInputUsdPerMillion !== undefined ? { cacheWriteInputUsdPerMillion: pricing.cacheWriteInputUsdPerMillion } : {}),
    ...(pricing.marginMultiplier === 1
      ? {}
      : { marginMultiplier: pricing.marginMultiplier })
  };
}

/** `credits` is present or absent as a pair — the console does not always know
 *  the current `usdPerCredit`, and half a preview would be worse than none. */
export type PricingPreview =
  | { free: true }
  | {
      free: false;
      credits?: { inputPerMillion: number; outputPerMillion: number };
    };

/**
 * Explanatory preview only — the server's calculation is authoritative.
 *
 * The margin lands on credits, not on the recorded provider cost: a settled
 * event's `costMicroUsd` is the raw spend and `creditMillis` is what the customer
 * is charged. This mirrors that.
 */
export function pricingPreview(
  pricing: PartnerCustomModelPricing,
  usdPerCredit?: number
): PricingPreview {
  if (partnerPricingBillingMode(pricing) === 'byo-unbilled') {
    return { free: true };
  }
  if (!usdPerCredit || usdPerCredit <= 0) return { free: false };
  return {
    free: false,
    credits: {
      inputPerMillion:
        (pricing.inputUsdPerMillion * pricing.marginMultiplier) / usdPerCredit,
      outputPerMillion:
        (pricing.outputUsdPerMillion * pricing.marginMultiplier) / usdPerCredit
    }
  };
}

/** The form fields a diagnostic result was produced against. */
export interface TestSubjectSnapshot {
  litellmModelName: string;
  providerBaseUrl: string;
  providerApiKey: string;
  litellmBaseUrl: string;
}

/**
 * Has the form moved since this result was produced?
 *
 * A stale pass shown as current is the failure mode that matters: an operator
 * fixes a typo'd endpoint, sees the old green result, and saves believing it was
 * verified.
 */
export function isTestResultStale(
  taken: TestSubjectSnapshot | undefined,
  current: TestSubjectSnapshot
): boolean {
  if (!taken) return false;
  return (
    taken.litellmModelName !== current.litellmModelName ||
    taken.providerBaseUrl !== current.providerBaseUrl ||
    taken.providerApiKey !== current.providerApiKey ||
    taken.litellmBaseUrl !== current.litellmBaseUrl
  );
}

export interface CustomModelEditFormState {
  modelKey: string;
  displayName: string;
  description: string;
  litellmModelName: string;
  contextWindow: string;
  maxOutputTokens: string;
  inputUsdPerMillion: string;
  outputUsdPerMillion: string;
  cachedInputUsdPerMillion: string;
  cacheWriteInputUsdPerMillion: string;
  marginMultiplier: string;
  /** Always empty: the stored key is never returned to the browser. Typing one is
   *  what makes an edit a credential rotation. */
  providerApiKey: string;
  /** The stored provider endpoint when the service owns the credential backend
   *  and the URL is safe to echo; blank otherwise. */
  providerBaseUrl: string;
  /** Always empty. Blank keeps whichever LiteLLM endpoint the backend already
   *  points at — the service preserves the stored ref rather than resetting to
   *  the shared one. */
  litellmBaseUrl: string;
  capabilities: PartnerCustomModelSummary['capabilities'];
  thinkingLevels?: ThinkingLevel[];
  expectedRevision: number;
  /** Derived, never typed by the operator: the reserved id is
   *  `bk_<partnerId>_<modelKey}`, and the create form's "backendId OR
   *  credential" submit condition otherwise forces them to type it by hand on a
   *  credential-less edit. */
  backendId: string;
  /** A record predating the pricing contract. The form requires rates before it
   *  will save one. */
  legacyWithoutPricing: boolean;
  /** `byo-token-budget` records cannot be edited at all — converging the mode
   *  would drop the gateway's raw-token cap. */
  blockedByBillingMode: boolean;
}

/**
 * Build the edit form from the redacted summary.
 *
 * The provider base URL is restored from `summary.providerBaseUrl`, which the
 * platform list read resolves for a service-generated credential backend when the
 * stored URL is safe to echo.
 *
 * Three fields stay blank because nothing returns them, and blank means "keep
 * what is stored" for all three: the API key, the LiteLLM model name (in
 * `SECRET_FIELD_NAMES`, so the HTTP response guard rejects any payload carrying
 * it), and the dedicated LiteLLM base URL. None is derived — a derived-but-wrong
 * value would be saved back verbatim.
 */
export function editFormStateFrom(
  summary: PartnerCustomModelSummary,
  partnerId: string
): CustomModelEditFormState {
  const pricing = summary.pricing;
  return {
    modelKey: summary.modelKey,
    displayName: summary.displayName,
    description: summary.description ?? '',
    litellmModelName: '',
    contextWindow: String(summary.limits.contextWindow),
    maxOutputTokens: String(summary.limits.maxOutputTokens),
    inputUsdPerMillion: pricing ? String(pricing.inputUsdPerMillion) : '',
    outputUsdPerMillion: pricing ? String(pricing.outputUsdPerMillion) : '',
    cachedInputUsdPerMillion: pricing?.cachedInputUsdPerMillion === undefined ? '' : String(pricing.cachedInputUsdPerMillion),
    cacheWriteInputUsdPerMillion: pricing?.cacheWriteInputUsdPerMillion === undefined ? '' : String(pricing.cacheWriteInputUsdPerMillion),
    marginMultiplier: pricing ? String(pricing.marginMultiplier) : '1',
    providerApiKey: '',
    providerBaseUrl: summary.providerBaseUrl ?? '',
    litellmBaseUrl: '',
    capabilities: summary.capabilities,
    ...(summary.thinkingLevels ? { thinkingLevels: [...summary.thinkingLevels] } : {}),
    expectedRevision: summary.revision,
    backendId: `bk_${partnerId}_${summary.modelKey}`,
    legacyWithoutPricing: !pricing,
    blockedByBillingMode: summary.billingMode === 'byo-token-budget'
  };
}

/** How the list column renders a model's pricing. */
export type PricingDisplay =
  | { kind: 'free' }
  | {
      kind: 'paid';
      inputUsdPerMillion: number;
      outputUsdPerMillion: number;
      cachedInputUsdPerMillion?: number;
      cacheWriteInputUsdPerMillion?: number;
      marginMultiplier: number;
    }
  | { kind: 'legacy'; billingMode: BillingMode };

/** A row with no `pricing` is legacy and is labelled as such rather than being
 *  shown as free — the two settle differently and conflating them hides which
 *  models still need rates entered. */
export function pricingDisplayFor(
  summary: Pick<PartnerCustomModelSummary, 'pricing' | 'billingMode'>
): PricingDisplay {
  if (!summary.pricing) {
    return { kind: 'legacy', billingMode: summary.billingMode ?? 'byo-unbilled' };
  }
  if (partnerPricingBillingMode(summary.pricing) === 'byo-unbilled') {
    return { kind: 'free' };
  }
  return {
    kind: 'paid',
    inputUsdPerMillion: summary.pricing.inputUsdPerMillion,
    outputUsdPerMillion: summary.pricing.outputUsdPerMillion,
    ...(summary.pricing.cachedInputUsdPerMillion !== undefined ? { cachedInputUsdPerMillion: summary.pricing.cachedInputUsdPerMillion } : {}),
    ...(summary.pricing.cacheWriteInputUsdPerMillion !== undefined ? { cacheWriteInputUsdPerMillion: summary.pricing.cacheWriteInputUsdPerMillion } : {}),
    marginMultiplier: summary.pricing.marginMultiplier
  };
}

/**
 * Can the operator run a diagnostic from the current form state?
 *
 * The API accepts `{modelKey}` XOR `{draft}`. A third form (modelKey plus draft
 * overrides) was rejected — it would widen where the stored secret gets used.
 *
 * `rotateCredential` is true exactly when an API key has been typed, which is the
 * only way a draft becomes possible: the stored key is never in the browser.
 *
 * On an edit without rotation the `{modelKey}` test exercises the STORED LiteLLM
 * model name. A name typed into the form is therefore an unsaved change the test
 * would not cover, and there is nothing to compare it against — the stored name
 * is in `SECRET_FIELD_NAMES`, so no response may carry it. Blocking with a reason
 * beats silently testing something else; clearing the field re-enables the test.
 */
export function testAvailability(form: {
  mode: 'create' | 'edit';
  rotateCredential: boolean;
  litellmModelName: string;
  providerApiKey: string;
  providerBaseUrl: string;
}):
  | { kind: 'draft' }
  | { kind: 'saved' }
  | { kind: 'unavailable'; reason: 'credentialNotInBrowser' | 'incompleteDraft' } {
  const draftReady = Boolean(
    form.providerApiKey.trim() &&
      form.providerBaseUrl.trim() &&
      form.litellmModelName.trim()
  );
  if (form.mode === 'create' || form.rotateCredential) {
    return draftReady
      ? { kind: 'draft' }
      : { kind: 'unavailable', reason: 'incompleteDraft' };
  }
  if (form.litellmModelName.trim()) {
    return { kind: 'unavailable', reason: 'credentialNotInBrowser' };
  }
  return { kind: 'saved' };
}
